import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { RefreshCw, Sparkles, ArrowRight, Wand2, Bot, Plus } from 'lucide-react'
import { PageHeader } from '@/components/dashboard-ui'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import api from '@/lib/api'
import {
  AgentDetailPanel,
  AgentMetricsHeader,
  FlowCanvas,
  type Assistant,
  type SampleConversationMessage
} from '@/components/agents'
import AgentBuilder from '@/components/agents/builder/AgentBuilder'
import MobileFAB from '@/components/layout/MobileFAB'

interface AgentMetrics {
  totalAgents: number
  callsThisWeek: number
  avgDurationSeconds: number
  positivePercent: number | null
  ratedCalls: number
  totalPhoneNumbers: number
}

interface AssistantWithStats extends Assistant {
  stats?: {
    callsThisWeek: number
    avgDurationSeconds: number
    lastCallAt?: string | null
    liveCallCount?: number
  }
  sampleConversation?: SampleConversationMessage[]
}

interface PhoneNumberRecord {
  id: string
  phoneNumber: string
  provider?: string
  status: string
  capabilities?: {
    inbound?: boolean
    outbound?: boolean
  }
  assistantId?: string
  assignedTo?: string
  outboundAssistantId?: string
  createdAt?: string
}

type StatsRange = '7d' | '30d' | '90d' | 'all'

export default function AIAgentsPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [loading, setLoading] = useState(true)
  const [timeframe, setTimeframe] = useState<StatsRange>('7d')
  const [assistants, setAssistants] = useState<AssistantWithStats[]>([])
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumberRecord[]>([])
  const [metrics, setMetrics] = useState<AgentMetrics>({
    totalAgents: 0,
    callsThisWeek: 0,
    avgDurationSeconds: 0,
    positivePercent: null,
    ratedCalls: 0,
    totalPhoneNumbers: 0
  })

  // Dialog states
  const [showCreateWizard, setShowCreateWizard] = useState(false)
  const [showConfigureWizard, setShowConfigureWizard] = useState(false)
  const [editingAssistant, setEditingAssistant] = useState<Assistant | null>(null)

  // Setup prompt state
  const [showSetupPrompt, setShowSetupPrompt] = useState(false)

  useEffect(() => {
    loadData()
    checkVoiceSetupStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-fetch stats when the user picks a different timeframe.
  useEffect(() => {
    if (!loading) loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe])

  // Auto-refresh while any agent's knowledge base is still training.
  // Backend polls Telnyx for up to ~6 min and flips kbUploadStatus to
  // 'success' or 'failed' — without this effect the UI would keep showing
  // "Training" until the user manually refreshed. Stops polling the
  // moment every agent is in a terminal state.
  useEffect(() => {
    const anyTraining = assistants.some(a => a.metadata?.kbUploadStatus === 'pending')
    if (!anyTraining) return
    const id = setInterval(() => {
      loadData()
    }, 10_000)
    // Safety cap — give up after 8 min, slightly more than the backend's
    // 6-min poll window, so we don't poll indefinitely on a stuck record.
    const stop = setTimeout(() => clearInterval(id), 8 * 60_000)
    return () => { clearInterval(id); clearTimeout(stop) }
  }, [assistants])

  // Check if voice wizard was completed during onboarding
  const checkVoiceSetupStatus = async () => {
    try {
      const res = await api.get('/api/voice/config')
      // Show prompt if wizard was not completed
      if (res.data.success && !res.data.config?.wizardCompleted) {
        setShowSetupPrompt(true)
      }
    } catch (error) {
      // If no config exists, show the prompt
      setShowSetupPrompt(true)
    }
  }

  const loadData = async () => {
    setLoading(true)
    try {
      const [assistantsRes, statsRes, numbersRes, tonePresetsRes, voiceConfigRes] = await Promise.all([
        api.get('/api/voice/assistants'),
        api.get(`/api/voice/assistants/stats?range=${timeframe}`).catch(() => ({ data: { success: false } })),
        api.get('/api/voice/numbers'),
        api.get('/api/voice/tone-presets').catch(() => ({ data: { success: false } })),
        api.get('/api/voice/config').catch(() => ({ data: { success: false } }))
      ])

      // Store full phone number records
      let allPhones: PhoneNumberRecord[] = []
      const phoneMap: Record<string, any[]> = {}
      const outboundPhoneMap: Record<string, any[]> = {}

      if (numbersRes.data.success) {
        allPhones = (numbersRes.data.phoneNumbers || []).filter((p: any) => p.status !== 'pending')
        allPhones.forEach((phone: any) => {
          if (phone.assistantId) {
            if (!phoneMap[phone.assistantId]) {
              phoneMap[phone.assistantId] = []
            }
            phoneMap[phone.assistantId].push({
              id: phone.id,
              phoneNumber: phone.phoneNumber,
              provider: phone.provider
            })
          }
          if (phone.outboundAssistantId) {
            if (!outboundPhoneMap[phone.outboundAssistantId]) {
              outboundPhoneMap[phone.outboundAssistantId] = []
            }
            outboundPhoneMap[phone.outboundAssistantId].push({
              id: phone.id,
              phoneNumber: phone.phoneNumber,
              provider: phone.provider
            })
          }
        })
      }
      setPhoneNumbers(allPhones)

      if (assistantsRes.data.success) {
        // Map stats to assistants
        const statsMap: Record<string, any> = {}
        if (statsRes.data.success && statsRes.data.assistantStats) {
          statsRes.data.assistantStats.forEach((stat: any) => {
            statsMap[stat.assistantId] = {
              callsThisWeek: stat.callsThisWeek || 0,
              avgDurationSeconds: stat.avgDurationSeconds || 0,
              connectedForms: stat.connectedForms || 0,
              lastCallAt: stat.lastCallAt || null,
              liveCallCount: stat.liveCallCount || 0,
            }
          })
        }

        // Build tone presets map for sampleConversation lookup. Each preset
        // now ships an inbound AND an outbound sample; we pick the right one
        // per agent based on its direction so inbound/outbound cards don't
        // show identical scripts.
        const tonePresetsMap: Record<string, { inbound: any[]; outbound: any[] }> = {}
        if (tonePresetsRes.data.success && tonePresetsRes.data.presets) {
          tonePresetsRes.data.presets.forEach((preset: any) => {
            tonePresetsMap[preset.id] = {
              inbound: preset.sampleConversation || [],
              outbound: preset.sampleConversationOutbound || preset.sampleConversation || []
            }
          })
        }

        // Get firm name and voice name from config
        const firmName = voiceConfigRes.data?.config?.greetingName || ''
        const configAgentName = voiceConfigRes.data?.config?.agentName || ''

        // Build assistant list
        const rawAssistants = assistantsRes.data.assistants.map((a: any) => {
          const tone = a.tonePreset || voiceConfigRes.data?.config?.tonePreset || 'balanced'
          const presetSamples = tonePresetsMap[tone] || { inbound: [], outbound: [] }
          const isOutbound = a.assistantType === 'outbound'
          const rawConversation = isOutbound ? presetSamples.outbound : presetSamples.inbound
          // Agent display name: use configured agent name, never expose voice name to users
          const agentDisplayName = configAgentName || a.agentName || 'AI Assistant'

          // Interpolate {firmName} and {agentName} placeholders
          const sampleConversation = rawConversation.map((msg: any) => ({
            ...msg,
            text: msg.text
              .replace(/\{firmName\}/g, firmName || a.name)
              .replace(/\{agentName\}/g, agentDisplayName)
          }))

          const agentPhones = isOutbound
            ? outboundPhoneMap[a.id] || []
            : phoneMap[a.id] || []

          return {
            ...a,
            phoneNumbers: agentPhones,
            stats: statsMap[a.id] || { callsThisWeek: 0, avgDurationSeconds: 0 },
            sampleConversation,
            voiceDisplayName: agentDisplayName
          }
        })

        setAssistants(rawAssistants)

        // Calculate aggregated metrics
        const totalCalls = rawAssistants.reduce(
          (sum: number, a: AssistantWithStats) => sum + (a.stats?.callsThisWeek || 0),
          0
        )
        const avgDuration =
          rawAssistants.length > 0
            ? rawAssistants.reduce(
                (sum: number, a: AssistantWithStats) => sum + (a.stats?.avgDurationSeconds || 0),
                0
              ) / rawAssistants.length
            : 0

        const totals = statsRes.data.success ? statsRes.data.totals : {}
        setMetrics({
          totalAgents: rawAssistants.length,
          callsThisWeek: totalCalls,
          avgDurationSeconds: Math.round(avgDuration),
          positivePercent: totals?.positivePercent ?? null,
          ratedCalls: totals?.ratedCalls ?? 0,
          totalPhoneNumbers: allPhones.length
        })
      }
    } catch (error) {
      console.error('Failed to load AI agents:', error)
      toast({
        title: 'Error',
        description: 'Failed to load AI agents',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (assistant: Assistant) => {
    setEditingAssistant(assistant)
  }

  const handlePreview = async (assistant: Assistant) => {
    toast({
      title: 'Voice Preview',
      description: `Loading ${assistant.name} voice sample...`
    })

    try {
      // Fetch audio from the voice preview API
      const response = await api.get(`/api/voice/preview/${assistant.voiceId || 'lauren'}`, {
        responseType: 'blob'
      })

      // Create audio element and play
      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' })
      const audioUrl = URL.createObjectURL(audioBlob)
      const audio = new Audio(audioUrl)

      audio.onended = () => {
        URL.revokeObjectURL(audioUrl)
      }

      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl)
        toast({
          title: 'Preview Error',
          description: 'Failed to play voice sample',
          variant: 'destructive'
        })
      }

      await audio.play()
    } catch (error: any) {
      console.error('Voice preview error:', error)
      toast({
        title: 'Preview Error',
        description: error.response?.data?.error || 'Failed to generate voice preview',
        variant: 'destructive'
      })
    }
  }

  const handleViewStats = (assistant: Assistant) => {
    navigate(`/calls?agentId=${assistant.id}&agentName=${encodeURIComponent(assistant.name)}`)
  }

  const handleViewInsights = (assistant: Assistant) => {
    navigate(`/agents/${assistant.id}/insights`)
  }

  const handleSaveEdit = (updated: Assistant) => {
    setAssistants((prev) =>
      prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a))
    )
    // Refetch full data to pick up phone assignments, webhook status, etc.
    loadData()
  }

  const [deleteConfirm, setDeleteConfirm] = useState<Assistant | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDelete = (assistant: Assistant) => {
    setDeleteConfirm(assistant)
  }

  const confirmDelete = async () => {
    if (!deleteConfirm) return
    setIsDeleting(true)
    try {
      await api.delete(`/api/voice/assistants/${deleteConfirm.id}`)
      toast({ title: 'Agent deleted', description: `${deleteConfirm.name} has been removed` })
      setDeleteConfirm(null)
      loadData()
    } catch (error: any) {
      toast({ title: 'Failed to delete agent', description: error.response?.data?.message || 'Something went wrong', variant: 'destructive' })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleAgentCreated = () => {
    loadData()
  }

  const handleClone = async (assistant: Assistant) => {
    try {
      const res = await api.post(`/api/voice/assistants/${assistant.id}/clone`)
      if (res.data?.success) {
        toast({
          title: 'Agent cloned',
          description: `${res.data.assistant?.name || 'Copy'} created (paused). Review and assign a phone number to activate.`,
        })
        loadData()
      } else {
        throw new Error(res.data?.message || 'Clone failed')
      }
    } catch (err: any) {
      toast({
        title: 'Failed to clone agent',
        description: err.response?.data?.message || err.message || 'Something went wrong',
        variant: 'destructive',
      })
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-64 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      <PageHeader
        eyebrow="Workforce"
        title="AI Agents"
        subtitle="Your AI team members and phone-number routing."
        icon={
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brand-royal/20 to-brand-periwinkle/15 border border-brand-royal/20 flex items-center justify-center">
            <Bot className="h-5 w-5 text-brand-royal" />
          </div>
        }
        actions={
          <>
            <Select value={timeframe} onValueChange={(v) => setTimeframe(v as StatsRange)}>
              <SelectTrigger className="h-9 w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
            {assistants.length > 0 && (
              <Button variant="outline" size="sm" onClick={() => setShowConfigureWizard(true)}>
                <Wand2 className="h-4 w-4 mr-2" />
                Edit wizard
              </Button>
            )}
            <Button variant="outline" size="icon" onClick={loadData} aria-label="Refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </>
        }
      />

      {/* Setup Prompt Banner — brand as accent (left stripe), not as fill. */}
      {showSetupPrompt && assistants.length === 0 && (
        <Card className="border-l-[3px] border-l-brand-royal">
          <CardContent className="py-5 sm:py-6">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl bg-brand-royal/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-5 w-5 text-brand-royal" strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-base tracking-tight">Complete your AI setup</h3>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                  We've analyzed your website and can create a personalized AI agent that knows your business. Set up your first agent to start handling calls automatically.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => setShowCreateWizard(true)}
                    className="bg-brand-royal hover:bg-brand-royal/90 text-white active:scale-[0.98] [transition:transform_140ms_cubic-bezier(0.23,1,0.32,1),background-color_200ms_ease-out]"
                  >
                    Create AI Agent
                    <ArrowRight className="h-4 w-4 ml-1.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowSetupPrompt(false)}>
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Metrics Header */}
      <AgentMetricsHeader
        metrics={metrics}
        loading={loading}
        rangeLabel={
          timeframe === '7d' ? 'Last 7 days'
          : timeframe === '30d' ? 'Last 30 days'
          : timeframe === '90d' ? 'Last 90 days'
          : 'All time'
        }
      />

      {/* Flow Visualization */}
      <FlowCanvas
        phoneNumbers={phoneNumbers}
        assistants={assistants}
        onRefresh={loadData}
        onEdit={handleEdit}
        onPreview={handlePreview}
        onViewStats={handleViewStats}
        onDelete={handleDelete}
        onClone={handleClone}
        onViewInsights={handleViewInsights}
        onCreateAgent={() => setShowCreateWizard(true)}
      />

      {/* Agent Builder — Create mode */}
      <AgentBuilder
        open={showCreateWizard}
        onClose={() => setShowCreateWizard(false)}
        onCreated={handleAgentCreated}
      />

      {/* Agent Builder — Configure/Edit mode */}
      <AgentBuilder
        open={showConfigureWizard}
        onClose={() => setShowConfigureWizard(false)}
        onCreated={handleAgentCreated}
        mode="configure"
      />

      {/* Edit Panel */}
      <AgentDetailPanel
        assistant={editingAssistant}
        open={!!editingAssistant}
        onClose={() => setEditingAssistant(null)}
        onSave={handleSaveEdit}
      />

      {/* Delete Agent Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Agent</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirm?.name}"? This will unassign it from any phone numbers and remove the voice configuration. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete Agent'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile FAB — primary action */}
      <MobileFAB
        icon={Plus}
        label="Create agent"
        onClick={() => setShowCreateWizard(true)}
      />
    </div>
  )
}
